import { useEffect } from "react";
import { gsap } from "gsap";
import { useMobileDetection } from "./MagicBento";
import "./MagicBento.css";

/**
 * App-wide MagicBento hover effects, applied by event delegation so every
 * button on every page gets them — including buttons that mount later —
 * without wrapping each one in a ParticleCard.
 *
 * Per button: cursor-tracked border glow ring, 3D tilt + magnetism, floating
 * particles while hovered, click ripple. Plus one document-level cursor
 * spotlight whose brightness follows proximity to the nearest button.
 *
 * Exclusions:
 *  - `.folder` and its papers (their look is driven by CSS transforms that an
 *    inline gsap transform would permanently override)
 *  - `.particle-container` (Home mode buttons — ParticleCard already runs
 *    these effects; they still receive glow vars from the spotlight pass)
 *  - tiny buttons (< 28px, e.g. whiteboard color swatches — the 6px glow ring
 *    would swamp them)
 *  - `[data-no-bento]` as a manual escape hatch
 */

const GLOW_COLOR = "99, 102, 241";
const SPOTLIGHT_RADIUS = 300;
const SELECTOR = 'button, [role="button"]';
const EXCLUDE_ANCESTORS = ".folder, [data-no-bento]";
const MIN_SIZE = 24;

const eligibleForGlow = (el, rect) =>
  rect.width >= MIN_SIZE &&
  rect.height >= MIN_SIZE &&
  !el.closest(EXCLUDE_ANCESTORS) &&
  !el.disabled;

const eligibleForPointerFx = el =>
  el &&
  !el.closest(EXCLUDE_ANCESTORS) &&
  !el.classList.contains("particle-container") &&
  !el.disabled;

export default function GlobalHoverEffects() {
  const isMobile = useMobileDetection();

  useEffect(() => {
    if (isMobile || window.matchMedia("(hover: none)").matches) return undefined;

    // ---- document-level cursor spotlight (same visual as MagicBento's) ----
    const spotlight = document.createElement("div");
    spotlight.className = "global-spotlight";
    spotlight.style.cssText = `
      position: fixed;
      width: 800px;
      height: 800px;
      border-radius: 50%;
      pointer-events: none;
      background: radial-gradient(circle,
        rgba(${GLOW_COLOR}, 0.15) 0%,
        rgba(${GLOW_COLOR}, 0.08) 15%,
        rgba(${GLOW_COLOR}, 0.04) 25%,
        rgba(${GLOW_COLOR}, 0.02) 40%,
        rgba(${GLOW_COLOR}, 0.01) 65%,
        transparent 70%
      );
      z-index: 200;
      opacity: 0;
      transform: translate(-50%, -50%);
      mix-blend-mode: screen;
    `;
    document.body.appendChild(spotlight);

    const proximity = SPOTLIGHT_RADIUS * 0.5;
    const fadeDistance = SPOTLIGHT_RADIUS * 0.75;

    let hovered = null;
    const stores = new WeakMap(); // per-button particle state

    const getStore = el => {
      let s = stores.get(el);
      if (!s) {
        s = { particles: [], timeouts: [] };
        stores.set(el, s);
      }
      return s;
    };

    const spawnParticles = el => {
      const s = getStore(el);
      const rect = el.getBoundingClientRect();
      const count = Math.max(3, Math.min(10, Math.round((rect.width * rect.height) / 6000)));
      for (let i = 0; i < count; i++) {
        const t = setTimeout(() => {
          if (hovered !== el || !el.isConnected) return;
          const p = document.createElement("div");
          p.className = "particle";
          p.style.cssText = `
            position: absolute;
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background: rgba(${GLOW_COLOR}, 1);
            box-shadow: 0 0 6px rgba(${GLOW_COLOR}, 0.6);
            pointer-events: none;
            z-index: 100;
            left: ${Math.random() * rect.width}px;
            top: ${Math.random() * rect.height}px;
          `;
          el.appendChild(p);
          s.particles.push(p);
          gsap.fromTo(p, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.3, ease: "back.out(1.7)" });
          gsap.to(p, {
            x: (Math.random() - 0.5) * 80,
            y: (Math.random() - 0.5) * 80,
            rotation: Math.random() * 360,
            duration: 2 + Math.random() * 2,
            ease: "none",
            repeat: -1,
            yoyo: true
          });
          gsap.to(p, { opacity: 0.3, duration: 1.5, ease: "power2.inOut", repeat: -1, yoyo: true });
        }, i * 100);
        s.timeouts.push(t);
      }
    };

    const clearParticles = el => {
      const s = stores.get(el);
      if (!s) return;
      s.timeouts.forEach(clearTimeout);
      s.timeouts = [];
      s.particles.forEach(p => {
        gsap.killTweensOf(p);
        gsap.to(p, {
          scale: 0,
          opacity: 0,
          duration: 0.3,
          ease: "back.in(1.7)",
          onComplete: () => p.parentNode?.removeChild(p)
        });
      });
      s.particles = [];
    };

    const enter = el => {
      hovered = el;
      if (getComputedStyle(el).position === "static") el.style.position = "relative";
      el.style.overflow = "hidden";
      spawnParticles(el);
      gsap.to(el, { rotateX: 5, rotateY: 5, duration: 0.3, ease: "power2.out", transformPerspective: 1000 });
    };

    const leave = el => {
      clearParticles(el);
      gsap.to(el, { rotateX: 0, rotateY: 0, x: 0, y: 0, duration: 0.3, ease: "power2.out" });
    };

    const onMouseOver = e => {
      const btn = e.target.closest(SELECTOR);
      if (!btn || btn === hovered || !eligibleForPointerFx(btn)) return;
      const rect = btn.getBoundingClientRect();
      if (rect.width < MIN_SIZE || rect.height < MIN_SIZE) return;
      if (hovered) leave(hovered);
      enter(btn);
    };

    const onMouseOut = e => {
      if (!hovered) return;
      const to = e.relatedTarget;
      if (to && (hovered === to || hovered.contains(to))) return;
      if (e.target === hovered || hovered.contains(e.target)) {
        leave(hovered);
        hovered = null;
      }
    };

    const onMouseMove = e => {
      // ---- glow ring vars + spotlight proximity, for every eligible button ----
      let minDistance = Infinity;
      document.querySelectorAll(SELECTOR).forEach(el => {
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        if (!eligibleForGlow(el, rect)) return;

        el.classList.add("magic-bento-card--border-glow");
        if (!el.style.getPropertyValue("--glow-color")) {
          el.style.setProperty("--glow-color", GLOW_COLOR);
        }

        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const distance =
          Math.hypot(e.clientX - centerX, e.clientY - centerY) - Math.max(rect.width, rect.height) / 2;
        const effectiveDistance = Math.max(0, distance);
        minDistance = Math.min(minDistance, effectiveDistance);

        let glowIntensity = 0;
        if (effectiveDistance <= proximity) glowIntensity = 1;
        else if (effectiveDistance <= fadeDistance) {
          glowIntensity = (fadeDistance - effectiveDistance) / (fadeDistance - proximity);
        }

        el.style.setProperty("--glow-x", `${((e.clientX - rect.left) / rect.width) * 100}%`);
        el.style.setProperty("--glow-y", `${((e.clientY - rect.top) / rect.height) * 100}%`);
        el.style.setProperty("--glow-intensity", glowIntensity.toString());
        el.style.setProperty("--glow-radius", `${SPOTLIGHT_RADIUS}px`);
      });

      gsap.to(spotlight, { left: e.clientX, top: e.clientY, duration: 0.1, ease: "power2.out" });
      const targetOpacity =
        minDistance <= proximity
          ? 0.8
          : minDistance <= fadeDistance
            ? ((fadeDistance - minDistance) / (fadeDistance - proximity)) * 0.8
            : 0;
      gsap.to(spotlight, { opacity: targetOpacity, duration: targetOpacity > 0 ? 0.2 : 0.5, ease: "power2.out" });

      // ---- tilt + magnetism on the hovered button ----
      if (hovered) {
        if (!hovered.isConnected) {
          hovered = null;
          return;
        }
        const rect = hovered.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        gsap.to(hovered, {
          rotateX: ((y - centerY) / centerY) * -10,
          rotateY: ((x - centerX) / centerX) * 10,
          x: (x - centerX) * 0.05,
          y: (y - centerY) * 0.05,
          duration: 0.1,
          ease: "power2.out",
          transformPerspective: 1000
        });
      }
    };

    const onClick = e => {
      const btn = e.target.closest(SELECTOR);
      if (!btn || !eligibleForPointerFx(btn)) return;
      const rect = btn.getBoundingClientRect();
      if (rect.width < MIN_SIZE || rect.height < MIN_SIZE) return;
      if (getComputedStyle(btn).position === "static") btn.style.position = "relative";
      btn.style.overflow = "hidden";

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const maxDistance = Math.max(
        Math.hypot(x, y),
        Math.hypot(x - rect.width, y),
        Math.hypot(x, y - rect.height),
        Math.hypot(x - rect.width, y - rect.height)
      );
      const ripple = document.createElement("div");
      ripple.style.cssText = `
        position: absolute;
        width: ${maxDistance * 2}px;
        height: ${maxDistance * 2}px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(${GLOW_COLOR}, 0.4) 0%, rgba(${GLOW_COLOR}, 0.2) 30%, transparent 70%);
        left: ${x - maxDistance}px;
        top: ${y - maxDistance}px;
        pointer-events: none;
        z-index: 1000;
      `;
      btn.appendChild(ripple);
      gsap.fromTo(
        ripple,
        { scale: 0, opacity: 1 },
        { scale: 1, opacity: 0, duration: 0.8, ease: "power2.out", onComplete: () => ripple.remove() }
      );
    };

    document.addEventListener("mouseover", onMouseOver, { passive: true });
    document.addEventListener("mouseout", onMouseOut, { passive: true });
    document.addEventListener("mousemove", onMouseMove, { passive: true });
    // Capture phase so the ripple shows even when a handler stops propagation.
    document.addEventListener("click", onClick, true);

    return () => {
      document.removeEventListener("mouseover", onMouseOver);
      document.removeEventListener("mouseout", onMouseOut);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("click", onClick, true);
      if (hovered) leave(hovered);
      hovered = null;
      gsap.killTweensOf(spotlight);
      spotlight.parentNode?.removeChild(spotlight);
    };
  }, [isMobile]);

  return null;
}
